import React from "react";

export const MATERIAL_ICON_SIZES = Object.freeze({
  compact: 18,
  standard: 24,
  large: 36,
  hero: 48
});

const ICONS = {
  signIn: { icon: "login", label: "Sign in" },
  signInGoogle: { icon: "login", label: "Sign in with Google" },
  leaderboard: { icon: "leaderboard", label: "Leaderboard" },
  leaderboardTab: { icon: "leaderboard", label: "Leaderboard" },
  book: { icon: "book", label: "Bible" },
  readingTab: { icon: "book", label: "Reading" },
  memorizeTab: { icon: "psychology", label: "Memorize" },
  deckLeaderboardTab: { icon: "leaderboard", label: "Deck leaderboard" },
  deckInfoTab: { icon: "info", label: "Deck information" },
  deckSettingsTab: { icon: "settings", label: "Deck settings" },
  createDeck: { icon: "add_circle", label: "Create deck" },
  joinDeck: { icon: "group_add", label: "Join deck" },
  leaveDeck: { icon: "logout", label: "Leave deck" },
  archiveDeck: { icon: "archive", label: "Archive deck" },
  shareDeck: { icon: "share", label: "Share deck" },
  copyDeck: { icon: "content_copy", label: "Copy deck link" },
  saveDeck: { icon: "save", label: "Save deck" },
  searchDecks: { icon: "search", label: "Search decks" },
  infoTab: { icon: "info", label: "Info" },
  settingsTab: { icon: "settings", label: "Settings" },
  bookList: { icon: "book_2", label: "Current challenges" },
  brain: { icon: "psychology", label: "Verse memorization" },
  addChallenge: { icon: "add_circle", label: "Create challenge" },
  joinChallenge: { icon: "group_add", label: "Join challenge" },
  personAdd: { icon: "person_add", label: "Add person" },
  archive: { icon: "archive", label: "View archived challenges" },
  search: { icon: "search", label: "Search" },
  searchOff: { icon: "search_off", label: "No results" },
  group: { icon: "group", label: "Participants" },
  archiveChallenge: { icon: "archive", label: "Archive challenge" },
  unarchiveChallenge: { icon: "unarchive", label: "Unarchive challenge" },
  calendar: { icon: "calendar_today", label: "No reading scheduled" },
  error: { icon: "error", label: "Error" },
  loadingBook: { icon: "menu_book", label: "Loading Bible text" },
  back: { icon: "arrow_back", label: "Back" },
  next: { icon: "arrow_forward", label: "Next" },
  close: { icon: "close", label: "Close" },
  check: { icon: "check_circle", label: "Completed" },
  view: { icon: "visibility", label: "View challenge" },
  add: { icon: "add", label: "Create challenge" },
  account: { icon: "account_circle", label: "Account" },
  warning: { icon: "warning", label: "Warning" },
  home: { icon: "home", label: "Home" },
  flag: { icon: "flag", label: "Save goal" },
  edit: { icon: "edit", label: "Edit" },
  removeModerator: { icon: "remove_moderator", label: "Remove moderator" },
  verifiedUser: { icon: "verified_user", label: "Moderator" },
  removePerson: { icon: "person_remove", label: "Remove participant" },
  share: { icon: "share", label: "Share challenge" },
  qrCode: { icon: "smartphone", label: "QR code" },
  emailEnabled: { icon: "mark_email_read", label: "Email updates enabled" },
  emailDisabled: { icon: "mark_email_unread", label: "Email updates disabled" },
  notificationsOn: { icon: "notifications", label: "Enable notifications" },
  notificationsOff: { icon: "notifications_off", label: "Disable notifications" },
  unarchive: { icon: "unarchive", label: "Unarchive" },
  leaveChallenge: { icon: "exit_to_app", label: "Leave challenge" },
  save: { icon: "save", label: "Save changes" },
  logout: { icon: "logout", label: "Sign out" },
  tune: { icon: "tune", label: "Preferences" },
  lightMode: { icon: "light_mode", label: "Light mode" },
  darkMode: { icon: "dark_mode", label: "Dark mode" },
  systemMode: { icon: "auto_mode", label: "System mode" },
  textSmall: { icon: "text_decrease", label: "Small text" },
  textMedium: { icon: "text_fields", label: "Medium text" },
  textLarge: { icon: "text_increase", label: "Large text" },
  installed: { icon: "check_circle", label: "App installed" },
  install: { icon: "download", label: "Install app" },
  installInfo: { icon: "info", label: "Install instructions" },
  help: { icon: "help", label: "Help" },
  info: { icon: "info", label: "About" },
  article: { icon: "article", label: "About this site" },
  email: { icon: "email", label: "Contact" },
  privacy: { icon: "privacy_tip", label: "Privacy policy" },
  notifications: { icon: "notifications", label: "Notifications" },
  iosShare: { icon: "ios_share", label: "Share menu" },
  menuMore: { icon: "more_vert", label: "Browser menu" },
  installMobile: { icon: "install_mobile", label: "Install app" },
  download: { icon: "download", label: "Download" },
  delete: { icon: "delete", label: "Delete" },
  volumeUp: { icon: "volume_up", label: "Test voice" },
  ttsPlay: { icon: "play_arrow", label: "Read aloud" },
  ttsPause: { icon: "pause", label: "Pause reading" },
  ttsResume: { icon: "play_arrow", label: "Resume reading" },
  streak: { icon: "local_fire_department", label: "Streak active" },
  incomplete: { icon: "close", label: "Incomplete" },
  bookmark: { icon: "bookmark", label: "Bookmark" },
  copy: { icon: "content_copy", label: "Copy verse" }
};

export const Icon = ({
  name,
  slot,
  size,
  style,
  className,
  label,
  ariaHidden = false
}) => {
  const definition = ICONS[name];
  const iconName = definition?.icon || name;
  const ariaLabel = label || definition?.label || name;
  const sizeValue = size ? MATERIAL_ICON_SIZES[size] : null;

  if (size && !sizeValue && process.env.NODE_ENV !== "production") {
    throw new Error(
      `Unknown Icon size "${size}". Use one of: ${Object.keys(MATERIAL_ICON_SIZES).join(", ")}.`
    );
  }

  const resolvedSize = sizeValue || (size ? MATERIAL_ICON_SIZES.standard : null);
  const resolvedStyle = resolvedSize
    ? {
      ...style,
      fontSize: `${resolvedSize}px`,
      width: `${resolvedSize}px`,
      height: `${resolvedSize}px`,
      lineHeight: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0
    }
    : style;

  if (ariaHidden) {
    return (
      <md-icon
        {...(slot ? { slot } : {})}
        {...(resolvedStyle ? { style: resolvedStyle } : {})}
        {...(className ? { className } : {})}
        aria-hidden="true"
      >
        {iconName}
      </md-icon>
    );
  }

  return (
    <md-icon
      {...(slot ? { slot } : {})}
      {...(resolvedStyle ? { style: resolvedStyle } : {})}
      {...(className ? { className } : {})}
      aria-label={ariaLabel}
    >
      {iconName}
    </md-icon>
  );
};
